/**
 * Collection table settings - persisted column visibility preferences
 */
import { atomWithStorage } from 'jotai/utils';

// ============================================================================
// Column Types
// ============================================================================

/**
 * All available columns in the collection table.
 * 14 existing + 2 new (cmc, manaCost)
 */
export type CollectionColumnKey =
  | 'image'
  | 'actions'
  | 'name'
  | 'supertype'
  | 'type'
  | 'subtype'
  | 'fullType'
  | 'colorIdentity'
  | 'cmc'
  | 'manaCost'
  | 'manaProduced'
  | 'set'
  | 'setNumber'
  | 'collector_number'
  | 'rarity'
  | 'released'
  | 'powerToughness'
  | 'power'
  | 'toughness'
  | 'quantity'
  | 'price'
  | 'folder'
  | 'condition'
  | 'language'
  | 'finish'
  | 'roles'
  | 'keywords'
  | 'edhrecRank'
  | 'gameChanger';

export interface CollectionColumnVisibility {
  image: boolean;
  actions: boolean;
  name: boolean;
  supertype: boolean;
  type: boolean;
  subtype: boolean;
  fullType: boolean;
  colorIdentity: boolean;
  cmc: boolean;
  manaCost: boolean;
  manaProduced: boolean;
  set: boolean;
  setNumber: boolean;
  collector_number: boolean;
  rarity: boolean;
  released: boolean;
  powerToughness: boolean;
  power: boolean;
  toughness: boolean;
  quantity: boolean;
  price: boolean;
  folder: boolean;
  condition: boolean;
  language: boolean;
  finish: boolean;
  roles: boolean;
  keywords: boolean;
  edhrecRank: boolean;
  gameChanger: boolean;
}

export interface CollectionTableSettings {
  columnVisibility: CollectionColumnVisibility;
}

// ============================================================================
// Defaults
// ============================================================================

/**
 * Default column visibility settings.
 * Visible: image, actions, name, type, color, set, quantity, price
 * Hidden: collector_number, folder, condition, language, finish, rarity, cmc, manaCost
 */
export const DEFAULT_COLLECTION_COLUMN_VISIBILITY: CollectionColumnVisibility = {
  // Always visible (not toggleable)
  image: true,
  actions: true,
  name: true,
  // Visible by default
  type: true,
  colorIdentity: true,
  setNumber: true,
  quantity: true,
  price: true,
  condition: true,
  finish: true,
  rarity: true,
  // Hidden by default
  supertype: false,
  subtype: false,
  fullType: false,
  cmc: false,
  manaCost: false,
  manaProduced: false,
  collector_number: false,
  set: false,
  released: false,
  powerToughness: false,
  power: false,
  toughness: false,
  folder: false,
  language: false,
  roles: false,
  keywords: false,
  edhrecRank: false,
  gameChanger: false,
};

export const DEFAULT_COLLECTION_TABLE_SETTINGS: CollectionTableSettings = {
  columnVisibility: DEFAULT_COLLECTION_COLUMN_VISIBILITY,
};

export function resolveCollectionColumnVisibility(visibility: Partial<CollectionColumnVisibility>): CollectionColumnVisibility {
  return { ...DEFAULT_COLLECTION_COLUMN_VISIBILITY, ...visibility };
}

// ============================================================================
// Atoms
// ============================================================================

/** Persisted collection table settings */
export const collectionTableSettingsAtom = atomWithStorage<CollectionTableSettings>(
  'mtgcraft:collection-table-settings',
  DEFAULT_COLLECTION_TABLE_SETTINGS,
);

// ============================================================================
// Column Metadata
// ============================================================================

export const COLLECTION_COLUMN_LABELS: Record<CollectionColumnKey, string> = {
  image: 'Image',
  actions: 'Actions',
  name: 'Name',
  supertype: 'Supertype',
  type: 'Type',
  subtype: 'Subtype',
  fullType: 'Full Type',
  colorIdentity: 'Color Identity',
  cmc: 'CMC',
  manaCost: 'Mana Cost',
  manaProduced: 'Produces',
  set: 'Set',
  setNumber: 'Set #',
  collector_number: 'Collector #',
  rarity: 'Rarity',
  released: 'Released',
  powerToughness: 'P/T',
  power: 'Power',
  toughness: 'Toughness',
  quantity: 'Quantity',
  price: 'Price',
  folder: 'Folder',
  condition: 'Condition',
  language: 'Language',
  finish: 'Finish',
  roles: 'Roles',
  keywords: 'Keywords',
  edhrecRank: 'EDHREC Rank',
  gameChanger: 'Game Changer',
};

/**
 * Column groups for display in visibility dropdown.
 * Groups: Card (basic card info), Print (printing details), Instance (collection instance data), Value (pricing)
 */
export const COLLECTION_COLUMN_GROUPS: { label: string; columns: CollectionColumnKey[] }[] = [
  { label: 'Types', columns: ['supertype', 'type', 'subtype', 'fullType'] },
  { label: 'Roles & Keywords', columns: ['roles', 'keywords'] },
  { label: 'Meta', columns: ['edhrecRank', 'gameChanger'] },
  { label: 'Mana', columns: ['colorIdentity', 'cmc', 'manaCost', 'manaProduced'] },
  { label: 'Stats', columns: ['powerToughness', 'power', 'toughness'] },
  { label: 'Print', columns: ['set', 'setNumber', 'collector_number', 'rarity', 'released', 'price'] },
  { label: 'Instance', columns: ['quantity', 'folder', 'condition', 'language', 'finish'] },
];

/**
 * Columns that should always be visible and not toggleable.
 * Actions column is needed for star/list functionality.
 */
export const ALWAYS_VISIBLE_COLUMNS: CollectionColumnKey[] = ['image', 'name', 'actions'];

/**
 * Get count of visible optional columns (excluding always-visible columns)
 */
export function getVisibleOptionalCount(visibility: Partial<CollectionColumnVisibility>): number {
  return Object.entries(resolveCollectionColumnVisibility(visibility)).filter(
    ([key, isVisible]) => isVisible && !ALWAYS_VISIBLE_COLUMNS.includes(key as CollectionColumnKey),
  ).length;
}
